// useVersionCheck.ts - v12.10.20 (#1932 a page that starts stale reloads itself once) · 2026-10-24
// useVersionCheck.ts - v12.10.9.1 (#1281) · was v11.8.4.7 (J2)
// v12.10.9.1 (#1281, Tushar 10/6 - all three agreed): (1) the sign-in page and the moment after signing in load a newer
// version by themselves (latestVersion / reloadIfNewer, used by SignInFlow); (2) while someone works, the banner stays -
// no forced reload; (3) a tab that was hidden or unused for 30+ minutes reloads quietly when the person comes back, if
// a newer version is out (nothing is lost - they were away).
// Detects when a newer build has been deployed while this tab is still running an older one.
// The build emits /version.json (see vite.config emitVersionJson). We poll it with no-store
// caching on mount, on an interval, and whenever the tab regains focus/visibility, then compare
// to the version compiled into THIS bundle. No service worker needed (this SPA is not a PWA).
import { useEffect, useState } from 'react';

const CURRENT_VERSION: string =
  (import.meta as unknown as { env?: { VITE_APP_VERSION?: string } }).env?.VITE_APP_VERSION ?? '';

// v12.10.20 (#1932): a page that STARTS on an older bundle than the deployed one (a duplicated tab, Back / Forward or a restored
// tab that the browser served from its cache) reloads itself once, straight away - no "new version" bar on a page that was
// already stale when it opened. Once per deployed version per tab (sessionStorage), so it can never loop.
const STALE_AT_LOAD_MS = 15000;
function reloadIfStaleAtLoad(latest: string): boolean {
  if (!latest || !CURRENT_VERSION || latest === CURRENT_VERSION) return false;
  if (typeof performance !== 'undefined' && performance.now() > STALE_AT_LOAD_MS) return false;
  try {
    if (sessionStorage.getItem('vs_t2_stalereload') === latest) return false;
    sessionStorage.setItem('vs_t2_stalereload', latest);
  } catch { return false; }
  window.location.reload();
  return true;
}

const POLL_MS = 5 * 60 * 1000; // every 5 minutes
const QUIET_RELOAD_AFTER_MS = 30 * 60 * 1000;

function versionUrl(): string {
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  return `${base}version.json`;
}

/** v12.10.9.1 (#1281): the deployed version, or null when it cannot be read. */
export async function latestVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${versionUrl()}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data.version ?? null;
  } catch { return null; }
}

/** v12.10.9.1 (#1281): true when a newer version than this bundle is deployed. */
export async function newerVersionOut(): Promise<boolean> {
  const latest = await latestVersion();
  return !!latest && !!CURRENT_VERSION && latest !== CURRENT_VERSION;
}

/** v12.10.9.1 (#1281): on the sign-in page - reload once if this bundle is older than the deployed one. */
export async function reloadIfNewer(): Promise<void> {
  const latest = await latestVersion();
  if (!latest || !CURRENT_VERSION || latest === CURRENT_VERSION) return;
  try {
    if (sessionStorage.getItem('vs_t2_vreload') === latest) return;   // already tried for this version - no loop
    sessionStorage.setItem('vs_t2_vreload', latest);
  } catch { return; }
  window.location.reload();
}

export function useVersionCheck(): { updateAvailable: boolean; current: string; latest: string | null } {
  const [latest, setLatest] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // Resolve relative to the SPA's base path (NOT the API origin).
    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    const url = `${base}version.json`;

    async function check() {
      try {
        const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (active && data.version) {
          if (reloadIfStaleAtLoad(data.version)) return;   // v12.10.20 (#1932)
          setLatest(data.version);
        }
      } catch {
        /* offline or marker missing - ignore, try again next tick */
      }
    }

    check();
    const id = window.setInterval(check, POLL_MS);
    // v12.10.9.1 (#1281): away for 30+ minutes (tab hidden, or no activity in any tab) and a newer version is out ->
    // reload quietly on return.
    let hiddenAt = 0;
    const lastUse = (): number => { try { return Number(localStorage.getItem('vs_t2_last_activity') ?? 0) || 0; } catch { return 0; } };
    const onVisible = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      const awayMs = Math.max(hiddenAt ? Date.now() - hiddenAt : 0, lastUse() ? Date.now() - lastUse() : 0);
      hiddenAt = 0;
      if (awayMs >= QUIET_RELOAD_AFTER_MS) {
        void newerVersionOut().then((newer) => { if (newer) window.location.reload(); else check(); });
        return;
      }
      check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      active = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const updateAvailable = !!latest && !!CURRENT_VERSION && latest !== CURRENT_VERSION;
  return { updateAvailable, current: CURRENT_VERSION, latest };
}
